/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import type { ActionReducerMapBuilder } from "@reduxjs/toolkit";

import { stopDbsPolling } from "../common";
import type { SignInState } from "../signIn.types";
import { ERROR_CODE_BIND_FAILED, ERROR_CODE_UNKNOWN } from "../signIn.types";
import {
  DBS_ERROR_LP_EXPIRED,
  DBS_ERROR_POLLING_CANCELLED
} from "../signIn.constants";
import {
  completeDbsBinding,
  startDbsLicensePlateFlow,
  startDbsPolling
} from "../thunks/dbsLicensePlate.thunks";

/**
 * Case reducers the DBS screen dispatches directly. Both stop the polling
 * loop, which lives outside the store — see the DBS polling controller.
 */
export const dbsLicensePlateReducers = {
  cancelDbsPolling(state: SignInState): void {
    stopDbsPolling();
    if (state.dbsPollingStatus === "polling") {
      state.dbsPollingStatus = "idle";
    }
  },
  resetDbsState(state: SignInState): void {
    state.dbsFlowStatus = "idle";
    state.dbsToken = "";
    state.dbsTokenExpiration = 0;
    state.licensePlateCode = "";
    state.licensePlateCredential = "";
    state.pollingIntervalSeconds = 0;
    state.licensePlateExpirationTime = "";
    state.licensePlateBindingUrl = "";
    state.licensePlateBindingCredentialUrl = "";
    state.dbsJwtToken = "";
    state.dbsPollingStatus = "idle";
    state.dbsError = null;
    stopDbsPolling();
  }
};

/**
 * Reducers for the DBS license-plate binding flow: code generation,
 * the polling loop that waits for the user to bind, and the final
 * bind + provision step.
 */
export function registerDbsLicensePlateReducers(
  builder: ActionReducerMapBuilder<SignInState>
): void {
  builder.addCase(startDbsLicensePlateFlow.pending, (state) => {
    state.dbsFlowStatus = "claiming";
    // Clear any prior expired/error polling status so the "code expired"
    // message is dismissed and the polling-restart effect can re-fire for
    // the freshly requested code.
    state.dbsPollingStatus = "idle";
    state.dbsError = null;
  });
  builder.addCase(startDbsLicensePlateFlow.fulfilled, (state, action) => {
    state.dbsFlowStatus = "awaiting-user-bind";
    state.dbsToken = action.payload.dbsToken;
    state.dbsTokenExpiration = action.payload.dbsTokenExpiration;
    state.licensePlateCode = action.payload.licensePlate.licensePlateCode;
    state.licensePlateCredential =
      action.payload.licensePlate.licensePlateCredential;
    state.pollingIntervalSeconds =
      action.payload.licensePlate.pollingIntervalSeconds;
    state.licensePlateExpirationTime =
      action.payload.licensePlate.expirationTime;
    state.licensePlateBindingUrl = action.payload.licensePlateBindingUrl;
    state.licensePlateBindingCredentialUrl =
      action.payload.licensePlateBindingCredentialUrl;
    state.authCode = action.payload.licensePlate.licensePlateCode;
    // Build QR code URL from credential URL template
    if (action.payload.licensePlateBindingCredentialUrl !== "") {
      state.qrCodeUrl = action.payload.licensePlateBindingCredentialUrl.replace(
        "{credential}",
        action.payload.licensePlate.licensePlateCode
      );
    } else if (action.payload.licensePlateBindingUrl !== "") {
      state.qrCodeUrl = action.payload.licensePlateBindingUrl;
    }
  });
  builder.addCase(startDbsLicensePlateFlow.rejected, (state, action) => {
    state.dbsFlowStatus = "error";
    state.dbsError = {
      code: ERROR_CODE_UNKNOWN,
      message:
        action.payload !== undefined && action.payload !== null
          ? action.payload
          : "DBS flow failed",
      retryable: true
    };
  });
  builder.addCase(startDbsPolling.pending, (state) => {
    state.dbsPollingStatus = "polling";
  });
  builder.addCase(startDbsPolling.fulfilled, (state, action) => {
    state.dbsPollingStatus = "success";
    state.dbsJwtToken = action.payload;
    state.dbsFlowStatus = "binding";
  });
  builder.addCase(startDbsPolling.rejected, (state, action) => {
    const message: string =
      action.payload !== undefined && action.payload !== null
        ? action.payload
        : "Polling failed";
    if (message === DBS_ERROR_LP_EXPIRED) {
      state.dbsPollingStatus = "expired";
      state.dbsFlowStatus = "error";
      // Clear the stale code from the display and move focus to the
      // "Get a new Code" button so the user is directed to request a new one.
      state.authCode = "";
      state.focusedField = "getNewCode";
      state.dbsError = {
        code: "DBS.LICENSE_PLATE_INVALID",
        message: DBS_ERROR_LP_EXPIRED,
        retryable: true
      };
    } else if (message === DBS_ERROR_POLLING_CANCELLED) {
      state.dbsPollingStatus = "idle";
    } else {
      state.dbsPollingStatus = "error";
      state.dbsFlowStatus = "error";
      state.dbsError = {
        code: ERROR_CODE_UNKNOWN,
        message,
        retryable: true
      };
    }
  });
  builder.addCase(completeDbsBinding.pending, (state) => {
    state.dbsFlowStatus = "binding";
  });
  builder.addCase(completeDbsBinding.fulfilled, (state, action) => {
    state.dbsFlowStatus = "completed";
    state.domainToken = action.payload.domainToken;
    state.provisionResponse = action.payload.provisionResponse;
  });
  builder.addCase(completeDbsBinding.rejected, (state, action) => {
    state.dbsFlowStatus = "error";
    state.dbsError = {
      code: ERROR_CODE_BIND_FAILED,
      message:
        action.payload !== undefined && action.payload !== null
          ? action.payload
          : "Bind failed",
      retryable: true
    };
  });
}
